import type {
  ContributionConsumer,
  DetachableProps,
  DetachedSurfaceInfo,
  IModuleService,
  OverlayResult,
  ViewDetachOptions,
  ViewHostPresentation,
  ViewOverlayApi,
  ViewProps,
  ViewRenderKind,
  WindowControlsApi,
} from '@hibro/shared';
import { externalProcessKey, isOpenViewRequest } from '@hibro/shared';
import { Button, cn, Skeleton, useSkeletonPulse } from '@hibro/ui-client';
import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { navigateAway as navigateAwayFromDocument } from '../app-links/navigation-guard';
import { useViewEventBus } from '../event-bus/event-bus';
import { useT, useTranslator } from '../i18n/i18n';
import { activityBarOpenGeneration } from '../layout/activity/activity-bar-open';
import { useViewNavigation } from '../navigation/navigation';
import { type UiRegistry } from '../runtime/ui-registry';
import type { DetachBridge } from './detach';
import { type ExternalBridge, ExternalSurface } from './external-surface';
import { useHostVisible } from './host-visibility';
import { type IframeBridge, IframeSurface } from './iframe-surface';
import { useOverlay } from './overlay-context';
import { usePageViewLoading } from './page-view-loading';
import { subscribeViewOpened } from './staged-module-open';
import { useSurfaceHostedIn } from './surface-hosted-in-context';
import {
  isExternalSurface,
  isIframeSurface,
  isRemoteSurface,
} from './surface-kind';
import { SurfaceLoadingIndicator } from './surface-loading';
import { useUserActivation } from './user-activation-context';
import { createViewExternalApi } from './view-external';

function logSurfaceLoadFailure(viewId: string, reason: string): void {
  (
    globalThis as {
      hibroLog?: {
        append: (entry: {
          level: string;
          kind: string;
          message: string;
        }) => void;
      };
    }
  ).hibroLog?.append({
    level: 'error',
    kind: 'log',
    message: `settings view failed to load (${viewId}): ${reason}`,
  });
}

/** Preferences section whose UI chunk failed. The reason is logged; the pane stays empty. */
function SettingsViewQuietFailure({
  viewId,
  reason,
}: {
  viewId: string;
  reason: string;
}): ReactNode {
  useEffect(() => {
    logSurfaceLoadFailure(viewId, reason);
  }, [reason, viewId]);
  return null;
}

/**
 * A view whose UI chunk failed to load. The message stays visible; Reload
 * refreshes the document. Retrying the same `import()` cannot recover a
 * content-hashed `/_assets` file from the previous deploy, and a rejected
 * activation stays cached for this document. Preferences sections log the
 * failure and render nothing.
 */
function SurfaceLoadError({
  message,
  reloadLabel,
}: {
  message: string;
  reloadLabel: string;
}): ReactNode {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 p-2">
      <p className="text-destructive">{message}</p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        {reloadLabel}
      </Button>
    </div>
  );
}

/**
 * Activity-bar custom views activate after the rail has already painted.
 * The surface spinner is `flex-1`, so it stretches the row until the view
 * arrives. `showExpandedTitle: false` reserves the icon-only 36 circle
 * (Figma 200:38562), inset `ml-[9px]` so it shares the icon centerline;
 * a slot that shows a title reserves the 36 glyph row. The host does not
 * look at which module owns the view.
 */
function ActivityBarSlotSkeleton({
  expanded,
  showExpandedTitle,
  label,
}: {
  expanded: boolean;
  showExpandedTitle: boolean;
  label: string;
}): ReactNode {
  const pulse = useSkeletonPulse(true);
  const iconOnly = !showExpandedTitle;
  return (
    <div
      role="status"
      aria-label={label}
      className={cn(
        'flex w-full shrink-0',
        iconOnly || expanded ? 'justify-start' : 'justify-center',
      )}
    >
      <Skeleton
        className={cn(
          'shrink-0',
          pulse ? undefined : 'animate-none',
          iconOnly
            ? 'ml-[9px] size-9 rounded-full'
            : expanded
              ? 'h-9 w-full rounded-xl'
              : 'size-9 rounded-xl',
        )}
      />
    </div>
  );
}

export interface SurfaceViewProps {
  viewId: string;
  moduleId: string;
  registry: UiRegistry;
  moduleService: IModuleService;
  /**
   * True while this surface is mounted but not visible — a kept-alive editor
   * cache entry (架构方案 §5.7 视图缓存). Surfaced to a source view as
   * `ViewProps.visible` so it can pause polling/animation; not a lifecycle
   * signal (activation still follows mount/unmount, §5.4).
   *
   * Only covers the *host's own* stacking (which pane of `EditorSlot` is on
   * top). Whether that host is itself on screen comes from
   * {@link useHostVisible}; the two are combined into `ViewProps.visible`.
   */
  hidden?: boolean;
  /** Whether this view may be popped out into a standalone window (§5.8). */
  detachable?: boolean;
  /**
   * Pop this view into its own window. Injected from the app shell (so platform
   * packages stay free of the preload `window.hibroWindow` bridge); rendered as a
   * small action only when `detachable` is true and a handler is provided.
   *
   * Carries `instanceKey` so an editor view with several instance tabs open
   * (§5.7 视图多实例) detaches the one that was clicked rather than the view as
   * a whole; absent for a singleton, which keeps the historical handle shape.
   */
  onDetach?: (viewId: string, moduleId: string, instanceKey?: string) => void;
  /** The view's render kind; picks the render surface (架构方案 §5.8). */
  kind?: ViewRenderKind;
  /** Content URL for `iframe` views (见 9). */
  url?: string;
  /** Bridge for in-DOM `iframe` surfaces (见 9; injected by the shell). */
  iframeBridge?: IframeBridge;
  /** Bridge to the main-process external-process host (injected by the shell, §5.8). */
  externalBridge?: ExternalBridge;
  /**
   * For `kind: 'external'` views: whether the control panel spawns the process
   * on mount (架构方案 §3.1/§5.8; default lazy). From `ResolvedView.autoLaunch`.
   */
  autoLaunch?: boolean;
  /**
   * For `kind: 'external'` views: which `entry.external` entry the panel drives
   * (架构方案 §3.1/§5.8). From `ResolvedView.externalEntry`; absent for a module
   * with a single entry, whose process key is the bare module id.
   */
  externalEntry?: string;
  /** Detach mechanism bridge (§5.8 通用 Detachable); becomes `ViewProps.detach`. */
  detachBridge?: DetachBridge;
  /**
   * Subscribe to contribution-point changes (见 17), injected by the host from
   * its `CONTRIBUTIONS_CHANGED_EVENT` listener. Backs `ViewProps.contributions.
   * onDidChangeContributions`; when absent that method degrades to a no-op.
   */
  subscribeContributionsChanged?: (listener: () => void) => () => void;
  /**
   * Present only when this view is rendered *inside* an OverlaySurface (架构方案
   * §5.8): lets the hosted view close its overlay with (or without) a result via
   * `ViewProps.overlay.resolve/dismiss`. Injected by the `OverlayHost`.
   */
  overlayControl?: {
    resolve: (result?: OverlayResult) => void;
    dismiss: () => void;
    /** 见 {@link ViewOverlayApi.dismissable}。承载时必有。 */
    dismissable: boolean;
  };
  /**
   * Opaque params for a view hosted inside an OverlaySurface (架构方案 §5.8),
   * surfaced to the view as `ViewProps.params`. Injected by the `OverlayHost`.
   */
  overlayParams?: unknown;
  /**
   * Instance discriminator when several instances of the same `viewId` are open
   * (架构方案 §5.7 视图多实例) — e.g. a chat `sessionId`. Surfaced to the view
   * as `ViewProps.instanceKey`; `undefined` for a singleton view.
   */
  instanceKey?: string;
  /** Host-owned presentation context for custom embedded views. */
  hostPresentation?: ViewHostPresentation;
  /**
   * Optional stand-in while this inline view's UI module is still activating.
   * Hosts whose slot has a known size (activityBar rail) pass a same-size
   * skeleton so the shell does not jump; omitted keeps the shared spinner.
   */
  loadingFallback?: ReactNode;
  /**
   * Preferences-window sections. A failed UI load is logged and the section
   * renders nothing.
   */
  concealLoadError?: boolean;
  /**
   * Open/focus an editor view instance (架构方案 §5.7 视图多实例). When present,
   * the view's `executeCommand` honors an {@link isOpenViewRequest} result by
   * routing it here — mirroring the workbench's activityBar `command` path — so a
   * view can open another instance (e.g. a chat session list switching tabs).
   * Absent outside the editor area (overlay/detached), where it is a no-op.
   */
  onOpenView?: (viewId: string, instanceKey?: string) => void;
}

/**
 * Build the detach capability handed to a source view via `ViewProps` /
 * `DetachableProps` (架构方案 §5.8 通用 Detachable): `detach`/`attach`/
 * `focusDetached` bound to this view + the reactive `detached` set (filtered to
 * this view, re-rendered on change). Delivered as plain props — no React context
 * across the shell↔module boundary — so `@hibro/ui-client` stays a pure UI lib
 * (见 16). No-op when no bridge is injected (e.g. web build).
 */
function useDetachCapability(
  viewId: string,
  moduleId: string,
  bridge?: DetachBridge,
): {
  detach: ViewProps['detach'];
  attach: ViewProps['attach'];
  focusDetached: ViewProps['focusDetached'];
  detached: DetachedSurfaceInfo[];
} {
  const [detached, setDetached] = useState<DetachedSurfaceInfo[]>([]);

  useEffect(() => {
    if (!bridge) {
      return;
    }
    let active = true;
    const apply = (surfaces: DetachedSurfaceInfo[]): void => {
      if (active) {
        setDetached(surfaces.filter((surface) => surface.viewId === viewId));
      }
    };
    void bridge.listDetached().then(apply);
    const dispose = bridge.onDetachedChange(apply);
    return () => {
      active = false;
      dispose();
    };
  }, [bridge, viewId]);

  const detach = useCallback<ViewProps['detach']>(
    async (options?: ViewDetachOptions) => {
      if (!bridge) {
        return;
      }
      await bridge.detachFragment({ viewId, moduleId, ...options });
    },
    [bridge, viewId, moduleId],
  );
  const attach = useCallback<ViewProps['attach']>(
    (handle) => bridge?.attach(handle) ?? Promise.resolve(),
    [bridge],
  );
  const focusDetached = useCallback<ViewProps['focusDetached']>(
    (handle) => bridge?.focus(handle) ?? Promise.resolve(),
    [bridge],
  );

  return { detach, attach, focusDetached, detached };
}

/**
 * Hold the document until a staged update of this module, if any, is the one
 * that will load. Keep-alive stays mounted while the view is hidden; becoming
 * visible again, or an explicit open, applies a package that landed in between
 * and remounts the document.
 */
function StagedDocumentGate({
  moduleId,
  viewId,
  hidden,
  moduleService,
  children,
}: {
  moduleId: string;
  viewId: string;
  hidden?: boolean;
  moduleService: IModuleService;
  children: (docToken: number) => ReactNode;
}): ReactNode {
  const t = useT();
  const visible = useHostVisible() && !hidden;
  const [openTick, setOpenTick] = useState(0);
  const [booted, setBooted] = useState(false);
  const [docToken, setDocToken] = useState(0);
  useEffect(
    () =>
      subscribeViewOpened((openedViewId) => {
        if (openedViewId === viewId) {
          setOpenTick((tick) => tick + 1);
        }
      }),
    [viewId],
  );
  useEffect(() => {
    if (!visible) {
      return;
    }
    let cancelled = false;
    void (async () => {
      let updated = false;
      try {
        updated = (await moduleService.applyStagedUpdate?.(moduleId)) === true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        (
          globalThis as {
            hibroLog?: {
              append: (entry: {
                level: string;
                kind: string;
                message: string;
              }) => void;
            };
          }
        ).hibroLog?.append({
          level: 'error',
          kind: 'log',
          message: `staged module update failed for ${moduleId}: ${message}`,
        });
      }
      if (cancelled) {
        return;
      }
      if (updated) {
        setDocToken((token) => token + 1);
      }
      setBooted(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, moduleId, moduleService, openTick]);
  if (!booted) {
    if (!visible) {
      return null;
    }
    return <SurfaceLoadingIndicator label={t('surface.view.loading')} />;
  }
  return children(docToken);
}

/**
 * SurfaceHost dispatcher (架构方案 §5.8). Picks the render surface by the view's
 * render `kind` (view-level, mixable within one module): `iframe` (with a URL +
 * bridge) mounts a sandboxed in-DOM `<iframe>` (见 9); `external` renders a
 * process control panel; `remote` is a reserved placeholder; everything else
 * renders the inline-dom component. Split into separate components so each calls
 * its hooks unconditionally.
 */
export function SurfaceView(props: SurfaceViewProps): ReactNode {
  const t = useT();
  const {
    kind,
    url,
    iframeBridge,
    externalBridge,
    autoLaunch,
    externalEntry,
    viewId,
    moduleId,
  } = props;
  // An `external` view has no inline content, so it must never fall through to
  // the inline-dom host (which would spin on "Loading view…" forever). Render
  // the process control panel, or a clear message if no bridge was injected.
  if (isExternalSurface(kind)) {
    return externalBridge ? (
      <StagedDocumentGate
        moduleId={moduleId}
        viewId={viewId}
        hidden={props.hidden}
        moduleService={props.moduleService}
      >
        {(docToken) => (
          <ExternalSurface
            key={docToken}
            processKey={externalProcessKey(moduleId, externalEntry)}
            bridge={externalBridge}
            autoLaunch={autoLaunch}
          />
        )}
      </StagedDocumentGate>
    ) : (
      <p className="p-2 text-muted-foreground">
        {t('surface.external.unavailable')}
      </p>
    );
  }
  // An `iframe` view (见 9) mounts a sandboxed `<iframe>` directly in the DOM.
  // `instanceKey` binds its gated connection to this instance (§5.7), so two
  // documents of one view neither share a port nor receive each other's
  // navigations.
  if (isIframeSurface(kind, url, !!iframeBridge) && iframeBridge && url) {
    return (
      <StagedDocumentGate
        moduleId={moduleId}
        viewId={viewId}
        hidden={props.hidden}
        moduleService={props.moduleService}
      >
        {(docToken) => (
          <IframeSurface
            key={docToken}
            viewId={viewId}
            instanceKey={props.instanceKey}
            url={url}
            bridge={iframeBridge}
          />
        )}
      </StagedDocumentGate>
    );
  }
  // `remote` is architecture-reserved (架构方案 §3): say so explicitly rather
  // than falling through to the inline-dom host and spinning forever (见 08).
  if (isRemoteSurface(kind)) {
    return (
      <p className="p-2 text-muted-foreground">
        {t('surface.remote.unavailable')}
      </p>
    );
  }
  return <InlineDomSurface {...props} />;
}

export interface FragmentSurfaceProps {
  viewId: string;
  moduleId: string;
  fragmentId: string;
  instanceKey?: string;
  payload?: unknown;
  registry: UiRegistry;
  moduleService: IModuleService;
  /** Detach bridge so a detached fragment can itself spawn/attach surfaces (§5.8). */
  detachBridge?: DetachBridge;
  /**
   * OS window controls for a bare (`chrome: false`) detached window (§5.8).
   * Injected into {@link DetachableProps.windowControls} so the fragment can
   * paint its own title bar / close button. Absent when platform chrome is on.
   */
  windowControls?: WindowControlsApi;
}

/**
 * Renders a detachable *fragment* of a source view (架构方案 §5.8 通用
 * Detachable). Activates the owning view's UI module (which runs
 * `registerDetachable`) then mounts the registered render source with the
 * `payload`/`instanceKey` identity. Reuses the same lazy activation +
 * reference-counted teardown as {@link InlineDomSurface}, and the same shared
 * host services, so a fragment window is a live projection (no serialized state).
 */
export function FragmentSurface({
  viewId,
  moduleId,
  fragmentId,
  instanceKey,
  payload,
  registry,
  moduleService,
  detachBridge,
  windowControls,
}: FragmentSurfaceProps): ReactNode {
  const translator = useTranslator();
  const t = translator.t;
  const hostedIn = useSurfaceHostedIn();
  const [renderer, setRenderer] = useState(() =>
    registry.getDetachable(fragmentId),
  );
  const [error, setError] = useState<string | null>(null);
  const detachCap = useDetachCapability(viewId, moduleId, detachBridge);

  const executeCommand = useCallback<DetachableProps['executeCommand']>(
    (id, ...args) => moduleService.executeCommand(id, ...args),
    [moduleService],
  );
  const openLink = useCallback<DetachableProps['openLink']>(
    (url) => moduleService.openLink?.(url, moduleId) ?? Promise.resolve(false),
    [moduleService, moduleId],
  );
  const fragmentT = useCallback<DetachableProps['t']>(
    (key, args) => translator.t(`${moduleId}:${key}`, args),
    [translator, moduleId],
  );

  useEffect(() => {
    let cancelled = false;
    // Activating the owning view activates its module, which registers both its
    // views and detachable fragments (架构方案 §5.4/§5.8).
    void moduleService.activateView(viewId).catch(() => undefined);
    registry
      .activateForView(viewId)
      .then(() => {
        if (!cancelled) {
          setRenderer(() => registry.getDetachable(fragmentId));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
      void moduleService.deactivateView(viewId).catch(() => undefined);
      void registry.deactivateForView(viewId).catch(() => undefined);
    };
  }, [viewId, fragmentId, registry, moduleService]);

  if (error) {
    return (
      <SurfaceLoadError
        message={t('surface.view.error', { message: error })}
        reloadLabel={t('surface.view.reload')}
      />
    );
  }
  if (!renderer) {
    return <SurfaceLoadingIndicator label={t('surface.view.loading')} />;
  }

  const props: DetachableProps = {
    moduleId,
    viewId,
    fragmentId,
    ...(instanceKey !== undefined ? { instanceKey } : {}),
    ...(payload !== undefined ? { payload } : {}),
    executeCommand,
    openLink,
    t: fragmentT,
    ...detachCap,
    hostedIn,
    ...(windowControls ? { windowControls } : {}),
  };
  return createElement(
    renderer as (props: DetachableProps) => ReactNode,
    props,
  );
}

/**
 * The `inline-dom` SurfaceHost (架构方案 §5.8). Renders a source-module view by
 * id: it triggers lazy UI activation through the `UiRegistry`, then mounts the
 * registered component as a plain React node so it composes freely with the DOM
 * shell.
 */
function InlineDomSurface({
  viewId,
  moduleId,
  registry,
  moduleService,
  hidden,
  detachable,
  onDetach,
  detachBridge,
  subscribeContributionsChanged,
  overlayControl,
  overlayParams,
  instanceKey,
  hostPresentation,
  onOpenView,
  externalBridge,
  loadingFallback,
  concealLoadError,
}: SurfaceViewProps): ReactNode {
  const translator = useTranslator();
  const t = translator.t;
  const hostedIn = useSurfaceHostedIn();
  const userActivation = useUserActivation();
  // Two independent ways to be off screen: hidden *within* this host (an
  // EditorSlot MRU pane behind the active one) or hosted by chrome that is
  // itself hidden (a dockview panel behind a sibling tab, §5.7 视图缓存).
  const visible = useHostVisible() && !hidden;
  const [component, setComponent] = useState(() =>
    registry.getViewComponent(viewId),
  );
  const [error, setError] = useState<string | null>(null);
  const detachCap = useDetachCapability(viewId, moduleId, detachBridge);
  // OverlaySurface openers from the workbench-root provider (架构方案 §5.8);
  // no-ops outside a provider (e.g. a detached window). `resolve`/`dismiss` are
  // present only when this view is itself hosted inside an overlay.
  const overlayApi = useOverlay();
  const overlay = useMemo<ViewOverlayApi>(
    () => ({
      ...overlayApi,
      resolve: overlayControl?.resolve,
      dismiss: overlayControl?.dismiss,
      ...(overlayControl ? { dismissable: overlayControl.dismissable } : {}),
    }),
    [overlayApi, overlayControl],
  );
  // Latest navigation location targeting this view *instance* (架构方案 §5.8
  // 跳转, 见 8): re-renders the view with the opaque `location` so it can
  // position itself. Per instance, so navigating one document does not move the
  // tab showing another (§5.7).
  const location = useViewNavigation(viewId, instanceKey);
  // Control over this module's own external processes (§3.1/§5.8), so a view can
  // carry its own launch/focus/close UI (a landing page that spawns on click)
  // rather than relying on the platform's default `external` panel. Scoped to
  // `moduleId`: module code passes an entry id, never a process key.
  const external = useMemo(
    () => createViewExternalApi(moduleId, externalBridge),
    [moduleId, externalBridge],
  );

  // Stable view callbacks: `moduleService`/`translator` are stable, so memoizing
  // keeps the same identity across parent re-renders. Without this, a view that
  // lists `executeCommand`/`t` in an effect's deps would re-run that effect on
  // every Workbench re-render (e.g. clicking another activityBar item), which
  // misfired the wallet's mount-time `showWalletApp()`.
  const executeCommand = useCallback<ViewProps['executeCommand']>(
    async (id, ...args) => {
      const result = await moduleService.executeCommand(id, ...args);
      // A command may return an OpenViewRequest to open/focus an editor view
      // instance (架构方案 §5.7 视图多实例) — same envelope the activityBar path
      // honors. Route it (when the editor host wired `onOpenView`), then still
      // return the raw result so the caller can read it.
      if (onOpenView && isOpenViewRequest(result)) {
        onOpenView(
          result.__appOpenView.viewId,
          result.__appOpenView.instanceKey,
        );
      }
      return result;
    },
    [moduleService, onOpenView],
  );
  const openLink = useCallback<ViewProps['openLink']>(
    (url) => moduleService.openLink?.(url, moduleId) ?? Promise.resolve(false),
    [moduleService, moduleId],
  );
  const resolveUrlOwner = useCallback<ViewProps['resolveUrlOwner']>(
    (url) =>
      moduleService.resolveUrlOwner?.(url, moduleId) ?? Promise.resolve(null),
    [moduleService, moduleId],
  );
  const navigateAway = useCallback<ViewProps['navigateAway']>(
    (url) => navigateAwayFromDocument(url),
    [],
  );
  // Module-scoped translator (见 14): the view passes its own bare key, we
  // resolve it against the module's `<moduleId>:<key>` catalog entries.
  const viewT = useCallback<ViewProps['t']>(
    (key, args) => translator.t(`${moduleId}:${key}`, args),
    [translator, moduleId],
  );
  // Contribution-point owners resolve another module's `%key%` data (见 14).
  // Scoped `t` cannot see `wallet:…`; `title` uses the contributor moduleId.
  const viewTitle = useCallback<ViewProps['title']>(
    (contributorModuleId, raw) => translator.title(contributorModuleId, raw),
    [translator],
  );
  // General EventBus subscription for this view (架构方案 §5.15): the pub/sub
  // counterpart of `executeCommand`, bound to the live channel by the host via
  // `ViewEventBusContext` (covers Workbench, DetachedSurface and overlay hosts).
  // Receipt policy follows the shared `canReceiveEvent` (§5.6/§5.15): own
  // namespace and non-protected events are receivable. Renderer views are
  // trusted in-process source code, so the protected-event declaration gate is
  // enforced at the sandboxed EventBus boundary (iframe/external), not here.
  // `subscribe` is stable per channel, so `onEvent` keeps a stable identity
  // across parent re-renders (same rationale as the memoized callbacks above).
  const subscribe = useViewEventBus();
  const onEvent = useCallback<ViewProps['onEvent']>(
    (event, handler) => subscribe(event, handler),
    [subscribe],
  );

  // Renderer-side contribution consumer (见 17): metadata over RPC; resolve a
  // contributor's renderer value by lazily activating its UI module and reading
  // the renderer-local registry; change subscription delegates to the host's
  // `CONTRIBUTIONS_CHANGED_EVENT` listener (no-op when not wired, e.g. edge
  // callers). Memoized so its identity is stable across parent re-renders.
  const contributions = useMemo<ContributionConsumer>(
    () => ({
      getContributions: (point) => moduleService.listModuleContributions(point),
      getCurations: (point) => moduleService.listContributionCurations(point),
      resolveContribution: async (entry) => {
        await registry.activateForModule(entry.moduleId);
        return registry.getContribution(entry.point, entry.key);
      },
      onDidChangeContributions: (_point, listener) =>
        subscribeContributionsChanged
          ? subscribeContributionsChanged(listener)
          : () => undefined,
    }),
    [moduleService, registry, subscribeContributionsChanged],
  );

  // This module's own declared settings (见 05 / 23), for a module carrying its own
  // settings page. `moduleId` comes from this view's registration rather than from
  // view code — the same path `viewT` above is scoped through — and the host
  // rejects a key the module did not declare. Rejects rather than no-ops when the
  // runtime left these verbs out (a gated connection): a settings page that
  // silently writes nowhere is worse than one that reports it cannot.
  const settings = useMemo<ViewProps['settings']>(
    () => ({
      get: (key) =>
        moduleService.getModuleSetting
          ? moduleService.getModuleSetting(moduleId, key)
          : Promise.reject(new Error('Module settings are not available here')),
      set: (key, value) =>
        moduleService.setModuleSetting
          ? moduleService.setModuleSetting(moduleId, key, value)
          : Promise.reject(new Error('Module settings are not available here')),
      reset: (key) =>
        moduleService.resetModuleSetting
          ? moduleService.resetModuleSetting(moduleId, key)
          : Promise.reject(new Error('Module settings are not available here')),
    }),
    [moduleService, moduleId],
  );

  useEffect(() => {
    let cancelled = false;
    // Activate the host-side service module in lockstep (onView activation,
    // 架构方案 §5.4 / 见 04). Fire-and-forget: it does not gate UI rendering
    // and a failure must not blank the view.
    void moduleService.activateView(viewId).catch(() => undefined);
    registry
      .activateForView(viewId)
      .then(() => {
        if (!cancelled) {
          setComponent(() => registry.getViewComponent(viewId));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
      // The view is no longer visible: deactivate its host service module and
      // UI module (架构方案 §5.4). Both are reference-counted and fail-soft, so
      // a module with other visible views stays active and errors never blank
      // the shell. (In React StrictMode dev, mount→unmount→remount causes a
      // transient deactivate+reactivate; production mounts once.)
      void moduleService.deactivateView(viewId).catch(() => undefined);
      void registry.deactivateForView(viewId).catch(() => undefined);
    };
  }, [viewId, registry, moduleService]);

  // Activity-bar slots and hosts that pass their own placeholder keep that
  // shape. A preferences page collects the rest into one spinner.
  const pageCollectsLoading = usePageViewLoading(
    viewId,
    !component &&
      error === null &&
      !loadingFallback &&
      hostPresentation?.kind !== 'activityBar',
  );

  if (error) {
    if (concealLoadError) {
      return <SettingsViewQuietFailure viewId={viewId} reason={error} />;
    }
    return (
      <SurfaceLoadError
        message={t('surface.view.error', { message: error })}
        reloadLabel={t('surface.view.reload')}
      />
    );
  }
  if (!component) {
    if (loadingFallback) {
      return loadingFallback;
    }
    if (hostPresentation?.kind === 'activityBar') {
      return (
        <ActivityBarSlotSkeleton
          expanded={hostPresentation.expanded}
          showExpandedTitle={hostPresentation.showExpandedTitle}
          label={t('surface.view.loading')}
        />
      );
    }
    if (pageCollectsLoading) {
      return null;
    }
    return <SurfaceLoadingIndicator label={t('surface.view.loading')} />;
  }

  const props: ViewProps = {
    moduleId,
    viewId,
    ...(hostPresentation ? { host: hostPresentation } : {}),
    executeCommand,
    openLink,
    resolveUrlOwner,
    navigateAway,
    userActivation,
    t: viewT,
    locale: translator.locale,
    title: viewTitle,
    // Opaque navigation location (见 8); `undefined` until navigated.
    location,
    onEvent,
    ...detachCap,
    // Renderer-side contribution consumer (见 17).
    contributions,
    // This module's own settings (见 05 / 23), for a module-owned settings page.
    settings,
    // OverlaySurface openers (+ self-close when hosted in an overlay, §5.8).
    overlay,
    // This module's own external processes (§3.1/§5.8) for a self-painted panel.
    external,
    // Opaque params when hosted in an overlay (§5.8); undefined when docked.
    params: overlayParams,
    // Instance discriminator for multi-instance views (§5.7); undefined = singleton.
    instanceKey,
    // Which window this rendering is mounted in (见 8). Fixed at mount.
    hostedIn,
    // False while kept alive off screen (§5.7 视图缓存) — behind another MRU
    // pane, or behind a sibling dockview tab — so the view can pause
    // polling/animation.
    visible,
    activityBarOpenGeneration: activityBarOpenGeneration(viewId),
  };
  // The contract types the component as `(props) => unknown` to keep `shared`
  // React-free; it is a real React FC, cast back here for rendering.
  const element = createElement(
    component as (props: ViewProps) => ReactNode,
    props,
  );

  if (!detachable || !onDetach) {
    return element;
  }

  // Overlay a small "detach" affordance for detachable views (§5.8). The inline
  // copy stays mounted; popping out opens a single-surface window sharing the
  // same host services. Out of scope: hiding the inline copy / `attach` back.
  // Fill the pane so module roots with `h-full` resolve (bare `relative` sizes
  // to content and collapses AI Thread to a top strip).
  return (
    <div className="relative h-full min-h-0 w-full">
      <Button
        variant="outline"
        size="icon-xs"
        title={t('surface.detach.title')}
        onClick={() => onDetach(viewId, moduleId, instanceKey)}
        className="absolute top-1 right-1 z-[1]"
      >
        ⧉
      </Button>
      {element}
    </div>
  );
}
